import { ErrorMessage } from "../components/Shared/Messages/ErrorMessage";
import { SuccessMessage } from "../components/Shared/Messages/SuccessMessage";
import { VAR_ACTIVE_TRACE_ID } from "../config/constants";
import { v3APIRequestFailureError } from "../lib/types/shared/v3.apiErrors";
import {
  v3HttpsStatusCodesWithMessages,
  v3StatusCodes,
  v3TranslatedDefaultErrorMessage
} from "../lib/types/shared/v3.httpsStatusCodes";
import { v3HandleAPIRequestsCommonProps } from "../lib/types/utils";

/**
 * Handles API requests with common logic for success and failure cases.
 *
 * @template RequestType - The type of the request payload.
 * @template ResponseType - The type of the expected response.
 *
 * @param {Object} args - The arguments object.
 * @param {function(RequestType): Promise<ResponseType>} args.request - The API request function.
 * @param {function(ResponseType): void} [args.onSuccess] - Callback function for successful API request.
 * @param {function(v3APIRequestFailureError): void} [args.onFailure] - Callback function for failed API request.
 * @param {boolean} [args.logErr=true] - Whether to log errors.
 * @param {string} [args.lang="en"] - The language for error messages (default: "en").
 * @param {...any} [args.params] - Additional parameters for the API request.
 *
 * @throws {Error} If an unexpected error occurs.
 *
 * @example
 * v3HandleAPIRequests<RequestType, ResponseType>({
 *   request: api.getSomeData,
 *   onSuccess: handleSuccess,
 *   onFailure: handleFailure,
 *   logErr: true,
 *   lang: "en",
 *   additionalParam: "someValue",
 * });
 */

const v3HandleAPIRequests = <RequestType, ResponseType>(
  args: v3HandleAPIRequestsCommonProps<RequestType, ResponseType>
): void => {
  const {
    request,
    onSuccess,
    onFailure,
    logErr = true,
    lang = "en",
    logSuccess = false,
    successMessage,
    errorMessage,
    ...params
  } = args;
  try {
    request({ ...params } as RequestType)
      .unwrap()
      .then((response: ResponseType) => {
        onSuccess && onSuccess(response as ResponseType);

        if (logSuccess) {
          const res = response as {
            message: string;
            translatedMessage: string;
          };
          SuccessMessage(
            successMessage ??
              ((lang === "en" ? res.message : res.translatedMessage) ||
                "Operation successful!")
          );
        }
      })
      .catch((err: v3APIRequestFailureError) => {
        if (onFailure) {
          onFailure?.(err);
        }

        if (logErr) {
          const traceId =
            localStorage.getItem(VAR_ACTIVE_TRACE_ID) || undefined;

          if (err?.status?.toString() === "FETCH_ERROR") {
            ErrorMessage(
              "Check your internet connection or contact the support team if this persists!"
            );
          } else if (!err?.data?.message) {
            if (!v3StatusCodes.includes(err?.status)) {
              ErrorMessage(
                errorMessage ?? v3TranslatedDefaultErrorMessage[lang],
                "error",
                traceId
              );
            } else {
              const similarCode = v3HttpsStatusCodesWithMessages.find(
                (code) => code.status === err.status
              );

              similarCode &&
                ErrorMessage(
                  errorMessage ?? similarCode[lang],
                  "error",
                  traceId
                );
            }
          } else {
            ErrorMessage(
              errorMessage ??
                (lang === "en" ? err.data.message : err.data.translatedMessage),
              "error",
              traceId
            );
          }
        }

        onFailure?.(err);
      });
  } catch (error) {
    ErrorMessage(errorMessage ?? v3TranslatedDefaultErrorMessage[lang]);
  }
};

export default v3HandleAPIRequests;
